import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { AppRoutingModule } from 'app/app-routing.module';
import { AppComponent } from 'app/app.component';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from "@app/shared/shared.module";
import { NavbarComponent } from 'app/core/components/navbar/navbar.component';
import { FooterComponent } from 'app/core/components/footer/footer.component';
import { ContentLayoutComponent } from '@app/core/layout/logged-in-layout/content-layout.component';
import { LayoutModule } from '@angular/cdk/layout';
import { CheckboxListComponent } from '@app/modules/products/components/checkbox-list/checkbox-list.component';
import { StoragesCheckboxListComponent } from '@app/modules/storages/components/checkbox-list/checkbox-list.component';
import { MatSidenavModule } from '@angular/material/sidenav';

import { NotLoggedInLayoutComponent } from '@app/core/layout/not-logged-in-layout/not-logged-in-layout.component';
import { TranslateModule, TranslateLoader} from '@ngx-translate/core';
import { TranslateHttpLoader} from '@ngx-translate/http-loader';
import { CookieService } from 'ngx-cookie-service';
import { InventoryDialogComponent } from '@app/modules/counts/inventory-dialog/inventory-dialog.component';
import { SelectReportComponent } from '@app/modules/counts/select-report/select-report.component';
import { FinancialComponent } from '@app/modules/counts/financial/financial.component';
import { InventoryCountsComponent } from '@app/modules/counts/inventory-counts/inventory-counts.component';
import { PoworksheetComponent } from '@app/modules/counts//poworksheet/poworksheet.component';
import { PsworksheetComponent } from '@app/modules/counts//psworksheet/psworksheet.component';
import { ExcelComponent } from '@app/modules/counts//excel/excel.component';
import { ReorderComponent } from '@app/modules/counts//reorder/reorder.component';
import { PrintCountComponent } from '@app/modules/counts//print-count/print-count.component';
import { CountHeaderComponent } from '@app/modules/counts/components/count-header/count-header.component';
import { UnsavedChangesGuard } from './modules/products/guards/unsaved-changes-guard.guard';
import { ImageUploadService } from './services/image-upload/image-upload.service';
import { InvoiceComponent } from '@app/modules/counts/invoice/invoice.component';
import { StoreModule } from '@ngrx/store';
import * as fromApp from './store/app.reducer';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { EffectsModule } from '@ngrx/effects';
import { ToastrModule } from 'ngx-toastr';
import { TrakrSuiteAutocompleteOffDirective } from './directives/trakr-suite-autocomplete-off.directive';
import { AppEffects } from '@app/store/app.effects';
import { InventoryRowDirective } from './directives/inventory-row.directive';
import { CameraScannerComponent } from '@app/modules/counts/components/camera-scanner/camera-scanner.component';
import { UpdateService } from './shared/components/quagga-scanner/update.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { Observable, tap } from 'rxjs';
import { AuthenticationStateService } from './core/services/authentication-state.service';
import { QuickBooksInterceptor } from './interceptors/quick-books/quick-books.interceptor';
import { QuickBooksService } from './modules/quick-books/services/quick-books.service';
import { TitleCasePipe } from '@angular/common';
import { UnauthorizedInterceptor } from './interceptors/unauthorized/unauthorized.interceptor';
import { BnNgIdleService } from 'bn-ng-idle';
import { PickingsCheckboxListComponent } from './modules/pickings/components/checkbox-list/checkbox-list.component';
import { ReceivingsCheckboxListComponent } from './modules/receivings/components/checkbox-list/checkbox-list.component';
import { PicklistsCheckboxListComponent } from './modules/picklists/components/checkbox-list/checkbox-list.component';
import { ReportListComponent } from './modules/reports/report-list/report-list.component';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}



@NgModule({
  declarations: [
    AppComponent,
    NavbarComponent,
    FooterComponent,
    ContentLayoutComponent,
    CheckboxListComponent,
    StoragesCheckboxListComponent,
    PickingsCheckboxListComponent,
    ReceivingsCheckboxListComponent,
    PicklistsCheckboxListComponent,
    NotLoggedInLayoutComponent,
    SelectReportComponent,
    InventoryDialogComponent,
    FinancialComponent,
    InventoryCountsComponent,
    PoworksheetComponent,
    PsworksheetComponent,
    ExcelComponent,
    ReorderComponent,
    PrintCountComponent,
    CountHeaderComponent,
    InvoiceComponent,
    TrakrSuiteAutocompleteOffDirective,
    InventoryRowDirective,
    CameraScannerComponent,
    ReportListComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    LayoutModule,
    SharedModule,
    MatSidenavModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    StoreModule.forRoot(fromApp.appReducer, { metaReducers: fromApp.metaReducers }),
    StoreDevtoolsModule.instrument({ 
      maxAge: 25,
      logOnly: environment.production,
     }),
    EffectsModule.forRoot(
      AppEffects
    ),
    ToastrModule.forRoot({ 
    positionClass: 'toast-bottom-full-width',  
    newestOnTop: true, 
    autoDismiss: true, 
    progressBar: true,
    timeOut: 2000
   }),
  ],
  exports: [
    TranslateModule,
    NavbarComponent,
    SharedModule,
    InventoryDialogComponent,
    FinancialComponent,
    InventoryCountsComponent,
    SelectReportComponent,
    PoworksheetComponent,
    PsworksheetComponent,
    ExcelComponent,
    ReorderComponent,
    PrintCountComponent,
    CountHeaderComponent,
    InvoiceComponent,
  ],
  providers: [
    CookieService,
    BnNgIdleService,
    UnsavedChangesGuard,
    UpdateService,
    TitleCasePipe,
    AuthenticationStateService,
    QuickBooksService,
    {
      provide: 'SITE_LOGO_SERVICE',
      useClass: ImageUploadService, 
      multi: true
    },
    {
      provide: 'REPORT_LOGO_SERVICE',
      useClass: ImageUploadService, 
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnauthorizedInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent]
})

export class AppModule { }